import React from "react";
import { ListboxButton } from "@headlessui/react";
import { VariantProps } from "class-variance-authority";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { HiChevronDown } from "react-icons/hi2";

import { dropdownSizeVariants } from "@/components/molecules/dropdown/Dropdown";

type Props<T> = VariantProps<typeof dropdownSizeVariants> & {
  leftPlaceholder?: React.ReactNode | string;
  hasError?: boolean;
  className?: string;
  selected: T[] | T | null;
  multiple?: boolean;
  displayValue?: (option: T) => React.ReactNode;
  placeholder?: string | React.ReactNode;
};

const CustomListboxButton = <T extends { id: string; name: string }>({
  leftPlaceholder,
  className,
  hasError,
  multiple,
  displayValue = (option) => option?.name,
  selected,
  placeholder,
  sizing,
}: Props<T>) => {
  const { t } = useTranslation();
  const displayedPlaceholder = placeholder || (multiple ? t("select.options") : t("select.option"));

  return (
    <ListboxButton
      className={clsx(
        dropdownSizeVariants({ sizing }),
        hasError ? "border-error-subtle" : "border-primary text-primary",
        className,
      )}
    >
      {leftPlaceholder && (
        <span className="block text-tertiary" data-testid={"left-placeholder"}>
          {leftPlaceholder}
        </span>
      )}
      {!selected || (multiple && Array.isArray(selected) && selected.length === 0) ? (
        <span className={"inline-flex flex-1 text-placeholder"}>{displayedPlaceholder}</span>
      ) : multiple && Array.isArray(selected) ? (
        <span className={"inline-flex flex-1 truncate"}>{selected.map(displayValue).join(", ")}</span>
      ) : (
        <span className={"inline-flex flex-1 truncate"}>{displayValue(selected as T)}</span>
      )}

      <HiChevronDown className="group pointer-events-none size-5 text-fg-secondary" aria-hidden="true" />
    </ListboxButton>
  );
};

export default CustomListboxButton;
